import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePopup } from "../../contexts/PopupContext";
import LeaveGeneratingReportModal from "./LeaveGeneratingReportModal";

const isReportsPath = (pathname) => pathname.startsWith("/reports");
const toPath = (loc) => `${loc.pathname}${loc.search || ""}${loc.hash || ""}`;

/**
 * Route-level "leave while a report is generating" guard.
 *
 * The app uses <BrowserRouter>, so react-router's blocker API isn't
 * available and navigation comes from many places (sidebar links, the
 * breadcrumb, search, the browser back button). Instead of patching each
 * one, MainInner renders <Routes location={…}> from the location this hook
 * returns: while a generation is streaming and the URL leaves /reports, the
 * previous location is *held* (so MainReports stays mounted and streaming)
 * and the confirm dialog is shown. "Keep generating" navigates back to the
 * held URL; "Stop and leave" releases the hold and the new route renders.
 *
 * Streaming state comes from MainReports via the "reports:generating" event
 * (same signal the reports sidebar uses for in-sidebar switches).
 */
export default function useGenerationLeaveGuard() {
  const realLocation = useLocation();
  const navigate = useNavigate();
  const { openPopUp, closePopUp, isPopUpOpen } = usePopup();

  const [generatingReportId, setGeneratingReportId] = useState(null);
  const generating = Boolean(generatingReportId);
  const [held, setHeldState] = useState(null);
  const heldRef = useRef(null);
  const prevLocationRef = useRef(realLocation);
  const decidedRef = useRef(false);
  const modalSeenOpenRef = useRef(false);

  const setHeld = (loc) => {
    heldRef.current = loc;
    setHeldState(loc);
  };

  useEffect(() => {
    const handler = (e) => setGeneratingReportId(e.detail?.reportId || null);
    window.addEventListener("reports:generating", handler);
    return () => window.removeEventListener("reports:generating", handler);
  }, []);

  const stay = useCallback(() => {
    const h = heldRef.current;
    decidedRef.current = true;
    closePopUp();
    if (h) navigate(toPath(h), { replace: true });
  }, [closePopUp, navigate]);

  const leave = useCallback(() => {
    decidedRef.current = true;
    closePopUp();
    // The reports sidebar drops the abandoned draft's "Untitled Report" row.
    window.dispatchEvent(
      new CustomEvent("reports:generation-abandoned", {
        detail: { reportId: generatingReportId },
      }),
    );
    setHeld(null);
  }, [closePopUp, generatingReportId]);

  // Intercept URL changes away from /reports while a generation streams.
  useEffect(() => {
    const nowOnReports = isReportsPath(realLocation.pathname);

    if (heldRef.current) {
      if (!generating) {
        // Generation finished (or was stopped) while the dialog was up:
        // nothing left to protect — let the navigation through.
        closePopUp();
        setHeld(null);
        prevLocationRef.current = realLocation;
      } else if (nowOnReports) {
        // "Keep generating" brought the URL back — release the hold.
        setHeld(null);
        prevLocationRef.current = realLocation;
      }
      return;
    }

    const prev = prevLocationRef.current;
    if (generating && isReportsPath(prev.pathname) && !nowOnReports) {
      decidedRef.current = false;
      modalSeenOpenRef.current = false;
      setHeld(prev);
      openPopUp(<LeaveGeneratingReportModal onStay={stay} onLeave={leave} />);
      return;
    }

    prevLocationRef.current = realLocation;
  }, [realLocation, generating, openPopUp, closePopUp, stay, leave]);

  // Dismissing the dialog via the backdrop counts as "keep generating".
  useEffect(() => {
    if (!heldRef.current) return;
    if (isPopUpOpen) {
      modalSeenOpenRef.current = true;
      return;
    }
    if (modalSeenOpenRef.current && !decidedRef.current) stay();
  }, [isPopUpOpen, stay]);

  // Tab close / refresh while generating: native browser prompt.
  useEffect(() => {
    if (!generating) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [generating]);

  return held || realLocation;
}
